'use client';

/**
 * Client-side purchase GTM helpers — lastCompletedOrder localStorage + dedupe.
 *
 * BEC-23431: purchase on order confirmation.
 */

import {
  resolveCheckoutCartGtmValue,
  resolveCheckoutCartTotalValue,
} from '@/lib/gtm/cartLineResolution';
import {
  buildPreOrderPurchaseEventExtras,
  resolvePreOrderPurchase,
  type PreOrderTransactionType,
} from '@/lib/gtm/preOrder';
import type {
  LastCompletedOrderSnapshot,
  PurchaseOrderFallback,
  PurchaseTrackingParams,
} from '@/lib/gtm/purchaseTracking.types';
import { extractPreorderData, type PreOrderData } from '@/lib/checkout/preOrder';
import { resolveOrderShowTax } from '@/lib/orders/preOrderOrder';
import type { OrderLine } from '@/lib/orders/fetchOrderData';
import type { CheckoutLine } from '@/states/checkout/checkoutState';
import { checkoutState } from '@/states/checkout';
import { paymentGatewaysState } from '@/states/paymentGatewaysState';

export type { LastCompletedOrderSnapshot, PurchaseOrderFallback, PurchaseTrackingParams };

const LAST_COMPLETED_ORDER_KEY = 'gtm_last_completed_order';
const PURCHASE_FIRED_PREFIX = 'gtm_purchase_fired_';

export interface CaptureLastCompletedOrderOptions {
  transactionId?: string;
  paymentTypes?: string[];
  transactionType?: PreOrderTransactionType;
  preOrderData?: PreOrderData | null;
  chargedAmount?: number;
  checkoutTotal?: number;
  showTax?: boolean;
  optimisticLines?: CheckoutLine[];
  channelSlug?: string | null;
  currency?: string;
}

function hasBrowserWindow(): boolean {
  return globalThis.window !== undefined;
}

export function resolvePaymentTypesForCapture(): string[] {
  const records = paymentGatewaysState.paymentRecords;
  if (records.length > 0) {
    return records.map((record) => record.type);
  }

  const selected = paymentGatewaysState.selectedMethod;
  return selected ? [selected] : [];
}

function resolvePreOrderCaptureFields(
  transactionType: PreOrderTransactionType,
  preOrderData: PreOrderData,
  chargedAmount: number,
  checkoutTotal: number,
  showTax: boolean
): Pick<LastCompletedOrderSnapshot, 'value' | 'transactionType' | 'eventExtras' | 'omitLineItems'> {
  const resolved = resolvePreOrderPurchase({
    transactionType,
    preOrderData,
    chargedAmount,
    showTax,
    checkoutTotal,
  });

  return {
    value: resolved.value,
    transactionType: resolved.transaction_type,
    omitLineItems: resolved.omitLineItems,
    eventExtras: buildPreOrderPurchaseEventExtras({
      transactionType,
      preOrderData,
      checkoutTotal,
      showTax,
    }),
  };
}

function shouldOmitPreOrderDepositTax(
  transactionType?: PreOrderTransactionType,
  omitLineItems?: boolean
): boolean {
  return omitLineItems === true && transactionType === 'DEPOSIT';
}

export function captureLastCompletedOrder(
  saleorOrderId: string,
  options?: CaptureLastCompletedOrderOptions
): void {
  if (!hasBrowserWindow()) {
    return;
  }

  try {
    const checkout = checkoutState.checkout;
    const paymentTypes = options?.paymentTypes ?? resolvePaymentTypesForCapture();
    const channelSlug =
      options?.channelSlug ?? checkout?.channel?.slug ?? checkoutState.channelSlug;
    const showTax = options?.showTax ?? resolveOrderShowTax(channelSlug);

    const fullCheckoutTotal = resolveCheckoutCartTotalValue(checkout, checkoutState.channelSlug);
    const preOrderGtmValue = resolveCheckoutCartGtmValue(checkout, checkoutState.channelSlug);
    const chargedAmount = options?.chargedAmount ?? preOrderGtmValue ?? fullCheckoutTotal ?? 0;
    const checkoutTotal = options?.checkoutTotal ?? fullCheckoutTotal ?? chargedAmount;

    const explicitTxType = options?.transactionType;
    const preOrderFromCheckout =
      explicitTxType === 'BALANCE' ? null : extractPreorderData(checkout);
    const preOrderData = options?.preOrderData ?? preOrderFromCheckout;

    const effectiveTxType: PreOrderTransactionType =
      explicitTxType ?? (preOrderData ? 'DEPOSIT' : 'FULL');

    let value = chargedAmount;
    let transactionType: PreOrderTransactionType = 'FULL';
    let eventExtras: Record<string, string | number> | undefined;
    let omitLineItems = false;

    if (preOrderData && effectiveTxType !== 'FULL') {
      const preOrderFields = resolvePreOrderCaptureFields(
        effectiveTxType,
        preOrderData,
        chargedAmount,
        checkoutTotal,
        showTax
      );
      value = preOrderFields.value;
      transactionType = preOrderFields.transactionType ?? 'FULL';
      eventExtras = preOrderFields.eventExtras;
      omitLineItems = preOrderFields.omitLineItems ?? false;
    }

    // JSON roundtrip instead of structuredClone — Valtio proxy objects cannot
    // be cloned via the structured clone algorithm (throws DOMException).
    const cloneArray = <T>(arr: T): T => JSON.parse(JSON.stringify(arr));

    let optimisticLines: CheckoutLine[] = [];
    if (options?.optimisticLines?.length) {
      optimisticLines = cloneArray(options.optimisticLines);
    } else if (checkoutState.lines?.length) {
      optimisticLines = cloneArray(checkoutState.lines);
    }

    const snapshot: LastCompletedOrderSnapshot = {
      saleorOrderId,
      transactionId: options?.transactionId,
      checkoutLines: checkout?.lines?.length ? cloneArray(checkout.lines) : undefined,
      optimisticLines,
      voucherCode: checkoutState.voucherCode ?? checkout?.voucherCode ?? null,
      value,
      currency:
        options?.currency ??
        checkout?.totalPrice?.gross?.currency ??
        checkoutState.currency ??
        'USD',
      tax: shouldOmitPreOrderDepositTax(transactionType, omitLineItems)
        ? undefined
        : checkout?.totalPrice?.tax?.amount,
      shipping: checkout?.shippingPrice?.gross?.amount,
      paymentTypes: paymentTypes.length > 0 ? paymentTypes : undefined,
      transactionType,
      eventExtras,
      omitLineItems,
    };

    globalThis.window.localStorage.setItem(LAST_COMPLETED_ORDER_KEY, JSON.stringify(snapshot));
  } catch {
    // Non-fatal — confirmation page falls back to order API data.
  }
}

export function readLastCompletedOrder(saleorOrderId: string): LastCompletedOrderSnapshot | null {
  if (!hasBrowserWindow()) {
    return null;
  }

  try {
    const raw = globalThis.window.localStorage.getItem(LAST_COMPLETED_ORDER_KEY);
    if (!raw) {
      return null;
    }

    const snapshot = JSON.parse(raw) as LastCompletedOrderSnapshot;
    return snapshot.saleorOrderId === saleorOrderId ? snapshot : null;
  } catch {
    return null;
  }
}

export function clearLastCompletedOrder(): void {
  if (!hasBrowserWindow()) {
    return;
  }

  try {
    globalThis.window.localStorage.removeItem(LAST_COMPLETED_ORDER_KEY);
  } catch {
    // Ignore storage errors.
  }
}

/** Returns true when purchase should fire (first time per order + transaction type in the tab). */
export function shouldFirePurchase(
  saleorOrderId: string,
  transactionType: PreOrderTransactionType = 'FULL'
): boolean {
  if (!hasBrowserWindow()) {
    return false;
  }

  const key = `${PURCHASE_FIRED_PREFIX}${saleorOrderId}:${transactionType}`;
  if (globalThis.window.sessionStorage.getItem(key)) {
    return false;
  }

  globalThis.window.sessionStorage.setItem(key, '1');
  return true;
}

export function orderLinesToOptimisticLines(lines: OrderLine[]): CheckoutLine[] {
  return lines.map((line, index) => ({
    id: `order-line-${index}`,
    variantId: '',
    sku: line.sku ?? '',
    name: line.name,
    price: line.quantity > 0 ? line.price / line.quantity : line.price,
    currency: line.currency,
    quantity: line.quantity,
  }));
}

export function buildPurchaseParamsFromSnapshot(
  snapshot: LastCompletedOrderSnapshot,
  transactionId: string
): PurchaseTrackingParams {
  return {
    checkoutLines: snapshot.checkoutLines,
    optimisticLines: snapshot.optimisticLines,
    transactionId: snapshot.transactionId ?? transactionId,
    value: snapshot.value,
    currency: snapshot.currency,
    tax: shouldOmitPreOrderDepositTax(snapshot.transactionType, snapshot.omitLineItems)
      ? undefined
      : snapshot.tax,
    shipping: snapshot.shipping,
    paymentTypes: snapshot.paymentTypes,
    coupon: snapshot.voucherCode ?? undefined,
    transactionType: snapshot.transactionType,
    eventExtras: snapshot.eventExtras,
    omitLineItems: snapshot.omitLineItems,
  };
}

export function buildPurchaseParamsFromCheckoutState(
  transactionId: string
): PurchaseTrackingParams | null {
  const checkout = checkoutState.checkout;
  if (!checkoutState.lines.length && !checkout?.lines?.length) {
    return null;
  }

  return {
    checkoutLines: checkout?.lines,
    optimisticLines: checkoutState.lines,
    transactionId,
    value: resolveCheckoutCartTotalValue(checkout, checkoutState.channelSlug) ?? 0,
    currency: checkout?.totalPrice?.gross?.currency ?? checkoutState.currency ?? 'USD',
    tax: checkout?.totalPrice?.tax?.amount,
    shipping: checkout?.shippingPrice?.gross?.amount,
    paymentTypes: resolvePaymentTypesForCapture(),
    coupon: checkoutState.voucherCode ?? checkout?.voucherCode ?? undefined,
  };
}

export function buildPurchaseParamsFromOrderFallback(
  fallback: PurchaseOrderFallback
): PurchaseTrackingParams | null {
  const pricing = fallback.pricing;
  const currency = pricing?.currency ?? fallback.lines[0]?.currency ?? 'USD';
  const preOrderSummary = pricing?.preOrder;
  const transactionType = fallback.transactionType ?? (preOrderSummary ? 'DEPOSIT' : 'FULL');
  const isPreOrderTx = transactionType === 'DEPOSIT' || transactionType === 'BALANCE';

  if (!fallback.lines.length && !isPreOrderTx) {
    return null;
  }

  if (preOrderSummary && isPreOrderTx) {
    const preOrderData = {
      deposit: preOrderSummary.deposit,
      deposit_net: preOrderSummary.deposit,
      deposit_percentage: preOrderSummary.depositPercentage,
    };
    const showTax = resolveOrderShowTax(fallback.channelSlug);
    const chargedAmount =
      transactionType === 'BALANCE' ? preOrderSummary.balance : preOrderSummary.deposit;
    const resolved = resolvePreOrderPurchase({
      transactionType,
      preOrderData,
      chargedAmount,
      checkoutTotal: preOrderSummary.total,
      showTax,
    });

    return {
      checkoutLines: undefined,
      optimisticLines: orderLinesToOptimisticLines(fallback.lines),
      transactionId: fallback.transactionId,
      value: resolved.value,
      currency,
      tax: shouldOmitPreOrderDepositTax(resolved.transaction_type, resolved.omitLineItems)
        ? undefined
        : pricing?.tax,
      shipping: pricing?.shipping,
      paymentTypes: fallback.paymentTypes.length > 0 ? fallback.paymentTypes : undefined,
      coupon: fallback.coupon ?? undefined,
      transactionType: resolved.transaction_type,
      omitLineItems: resolved.omitLineItems,
      eventExtras: buildPreOrderPurchaseEventExtras({
        transactionType: resolved.transaction_type,
        preOrderData,
        checkoutTotal: preOrderSummary.total,
        showTax,
      }),
    };
  }

  if (!fallback.lines.length) {
    return null;
  }

  return {
    checkoutLines: undefined,
    optimisticLines: orderLinesToOptimisticLines(fallback.lines),
    transactionId: fallback.transactionId,
    value: pricing?.total ?? fallback.lines.reduce((sum, line) => sum + line.price, 0),
    currency,
    tax: pricing?.tax,
    shipping: pricing?.shipping,
    paymentTypes: fallback.paymentTypes.length > 0 ? fallback.paymentTypes : undefined,
    coupon: fallback.coupon ?? undefined,
    transactionType: 'FULL',
  };
}

export function resolvePurchaseTrackingParams(
  saleorOrderId: string,
  fallback: PurchaseOrderFallback | null
): PurchaseTrackingParams | null {
  const transactionId = fallback?.transactionId ?? saleorOrderId;
  const stored = readLastCompletedOrder(saleorOrderId);
  if (stored) {
    return buildPurchaseParamsFromSnapshot(stored, transactionId);
  }

  const fromCheckout = buildPurchaseParamsFromCheckoutState(transactionId);
  if (fromCheckout?.optimisticLines.length) {
    return fromCheckout;
  }

  if (fallback) {
    return buildPurchaseParamsFromOrderFallback(fallback);
  }

  return null;
}
